import { IAM_SERVICE } from '@/@core/services/api/IamService';
import {
  EXTERNAL_REQUEST_HTTP_CLIENT,
  HTTP_CLIENT,
  HTTP_CLIENT_IAM,
} from '@/@core/services/api/httpClient';
import { APPLICATION_HTTP_CLIENT } from '@/@core/services/application/applicationHttpClient';
import { authManager } from '@/@core/services/auth/auth';
import { createStoreId } from '@/stores/createStoreId';
import { User } from 'oidc-client-ts';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

const MINUTES_TO_INVALIDATE_USER_BEFORE_SESSION_EXPIRES = 10;

export const useUserStore = defineStore(createStoreId('user'), () => {
  const user = ref<User | null>(null);
  const userId = computed(() => (user.value ? user.value.profile.sub : null));
  const hasAdminPermissions = ref<boolean | undefined>(undefined);
  const hasNuminousPermissions = ref<boolean | undefined>(undefined);

  async function hasPermission(adminPermission: string) {
    let permissions: { permission: string }[] = [];
    try {
      permissions = (await IAM_SERVICE.checkUser([adminPermission])).permissions;
    } catch {
      console.warn('Failed to check user privileges');
    }
    return permissions.some((perm: { permission: string }) => perm.permission === adminPermission);
  }

  async function isAdmin() {
    const adminPermission = 'AdministrateAssistant';
    if (user.value && hasAdminPermissions.value === undefined)
      hasAdminPermissions.value = await hasPermission(adminPermission);
    return hasAdminPermissions.value;
  }

  async function isNuminousUser() {
    const numinousPermission = 'NuminousAccess';
    if (user.value && hasNuminousPermissions.value === undefined)
      hasNuminousPermissions.value = await hasPermission(numinousPermission);
    return hasNuminousPermissions.value;
  }

  async function fetch() {
    if (!user.value) {
      user.value = await authManager.getUser();
      if (user.value !== null) {
        HTTP_CLIENT.setBearerToken(user.value.access_token);
        HTTP_CLIENT_IAM.setBearerToken(user.value.access_token);
        EXTERNAL_REQUEST_HTTP_CLIENT.setBearerToken(user.value.access_token);
        APPLICATION_HTTP_CLIENT.setBearerToken(user.value.access_token);
      }
    }
  }

  function isInvalidUserSession() {
    return (
      user.value === null ||
      user.value.expired ||
      (user.value.expires_at !== undefined &&
        user.value.expires_at <= MINUTES_TO_INVALIDATE_USER_BEFORE_SESSION_EXPIRES * 60)
    );
  }

  return {
    user,
    userId,
    isAdmin,
    isNuminousUser,
    fetch,
    isInvalidUserSession,
  };
});
